import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Scale, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import RichTextEditor from '../../components/admin/RichTextEditor';

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';
const bool = (v) => v === true || v === 'true' || v === 'True';

export default function LegalPageManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', content: '', position: 0, is_active: true });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchAll = useCallback(() => {
    api.get('/legal-pages').then(r => setPages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => { setForm({ title: '', slug: '', content: '', position: 0, is_active: true }); setEditing(null); setShowForm(false); };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title || '', slug: p.slug || '', content: p.content || '', position: parseInt(p.position) || 0, is_active: bool(p.is_active) });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Sayfa adı gerekli'); return; }
    setIsSubmitting(true);
    try {
      if (editing) { await api.put(`/legal-pages/${editing.id}`, form); toast.success('Güncellendi'); }
      else { await api.post('/legal-pages', form); toast.success('Oluşturuldu'); }
      resetForm(); setLoading(true); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu yasal sayfayı silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/legal-pages/${id}`); toast.success('Silindi'); setLoading(true); fetchAll(); }
    catch { toast.error('Hata'); }
  };

  return (
    <div data-testid="admin-legal-page-manager">
      <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Scale className="w-6 h-6 text-[hsl(var(--gold))]" /> Yasal Sayfalar</h1>
          <p className="text-sm text-muted-foreground mt-1">Gizlilik, KVKK, kullanım koşulları gibi yasal sayfalarınızı oluşturun. Aktif sayfalar footer'da otomatik gösterilir.</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-legal-page"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] shrink-0">
          <Plus className="w-4 h-4" /> Yeni Yasal Sayfa
        </button>
      </div>

      {showForm && (
        <div className="my-6 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Sayfa Düzenle' : 'Yeni Yasal Sayfa'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Sayfa Adı *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Örn: Gizlilik Sözleşmesi / KVKK / Kullanım Koşulları" className={inputCls} data-testid="legal-title" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Sıra</label>
                <input type="number" value={form.position} onChange={e => setForm(f => ({ ...f, position: parseInt(e.target.value) || 0 }))} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Sayfa İçeriği</label>
              <RichTextEditor value={form.content} onChange={(html) => setForm(f => ({ ...f, content: html }))} placeholder="Sayfanın metnini buraya yazın..." testId="legal-content" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[hsl(var(--gold))]" data-testid="legal-active" />
              Aktif (footer'da göster)
            </label>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="legal-save">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : pages.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground mt-4">Henüz yasal sayfa eklenmemiş</div>
      ) : (
        <div className="space-y-3 mt-6">
          {pages.map(p => (
            <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid="legal-page-item">
              <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0"><Scale className="w-4 h-4 text-[hsl(var(--gold))]" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{p.title}</div>
                <div className="text-xs text-muted-foreground">/yasal/{p.slug}</div>
              </div>
              <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${bool(p.is_active) ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-secondary text-muted-foreground'}`}>
                {bool(p.is_active) ? 'Aktif' : 'Pasif'}
              </span>
              <a href={`/yasal/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-secondary shrink-0" title="Görüntüle"><ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => handleEdit(p)} className="p-2 rounded-lg hover:bg-secondary shrink-0" title="Düzenle"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive shrink-0" title="Sil"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
